import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  StockMovementService,
  StockMovementRecord,
  StockMovementMeta,
} from './stock-movement.service';
import { ToastrService } from 'ngx-toastr';
import { InventoryService } from '../inventory/inventory.service';
import { ProductsService } from '../products/products.service';
import { withMatchedEan } from '../../core/utils/current-ean.util';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
  map,
  catchError,
} from 'rxjs/operators';
import { NgSelectModule } from '@ng-select/ng-select';

interface Store {
  id: string; // Ensure this matches the store object structure
  name: string;
}

interface Product {
  id: string; // This is the variantId
  sku: string;
  name: string;
  /** "Search by EAN" box: the code the typed term matched. */
  ean?: string;
}

@Component({
  selector: 'app-stock-movement',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NgSelectModule],
  templateUrl: './stock-movement.component.html',
  styleUrl: './stock-movement.component.scss',
})
export class StockMovementComponent implements OnInit {
  private stockMovementService = inject(StockMovementService);
  private inventoryService = inject(InventoryService);
  private productService = inject(ProductsService);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  public searchParams = {
    selectedProduct: null as Product | null,
    selectedStores: [] as Store[], // Changed to array for multi-select
    fromDate: this.getInitialDate('start'),
    toDate: this.getInitialDate('end'),
  };

  public productSearch$ = new Subject<string>();
  public productNameSearch$ = new Subject<string>();
  public productEanSearch$ = new Subject<string>();

  public products: Product[] = [];
  public nameSearchResults: Product[] = [];
  public eanSearchResults: Product[] = [];

  public isSearchingProduct = false;
  public isSearchingByName = false; // New loading state for name search
  public isSearchingByEan = false;

  public movementList: StockMovementRecord[] = [];
  public meta: StockMovementMeta | null = null;
  public stores: Store[] = []; // Changed from locations to stores

  public loading = false;
  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public pageCount = 1;
  public maxPageNumbers = 5;

  ngOnInit(): void {
    this.fetchStores(); // Fetch stores instead of locations
    this.setupSkuSearch();
    this.setupNameSearch(); // Initialize name search
    this.setupEanSearch();
  }

  private getInitialDate(type: 'start' | 'end'): string {
    const date = new Date();
    if (type === 'start') {
      date.setDate(1);
    }
    return date.toISOString().split('T')[0];
  }

  private setupSkuSearch(): void {
    this.productSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 3),
        tap(() => (this.isSearchingProduct = true)),
        switchMap((term) =>
          this.productService.searchBySKUWithContext(term, 'PRODUCT_LIST').pipe(
            map((res: any) => {
              const items = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res)
                ? res
                : [];
              return items.map((item: any) => ({
                id:
                  item.variants && item.variants.length > 0
                    ? item.variants[0].id
                    : item.id,
                sku: item.sku,
                name: item.name,
              }));
            }),
            catchError(() => of([])),
            tap(() => (this.isSearchingProduct = false))
          )
        )
      )
      .subscribe((data: Product[]) => (this.products = data));
  }

  // New method for name search setup
  private setupNameSearch(): void {
    this.productNameSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 3),
        tap(() => (this.isSearchingByName = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({
              query: term,
              context: 'PO', // Using PO context as requested
            })
            .pipe(
              map((res: any) => {
                const items = Array.isArray(res.data)
                  ? res.data
                  : Array.isArray(res)
                  ? res
                  : [];
                return items.map((item: any) => ({
                  id:
                    item.variants && item.variants.length > 0
                      ? item.variants[0].id
                      : item.id,
                  sku: item.sku,
                  name: item.name || item.longDescription, // Fallback to longDescription if name missing
                }));
              }),
              catchError(() => of([])),
              tap(() => (this.isSearchingByName = false))
            )
        )
      )
      .subscribe((data: Product[]) => (this.nameSearchResults = data));
  }

  /** EAN search — identity barcode or any price generation's EAN. */
  private setupEanSearch(): void {
    this.productEanSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isSearchingByEan = true)),
        switchMap((term) =>
          this.productService
            .searchProducts({ ean: term.trim(), context: 'PRODUCT_LIST' })
            .pipe(
              map((res: any) => {
                const items = Array.isArray(res?.data) ? res.data : [];
                return withMatchedEan(items, term).map((item: any) => ({
                  id:
                    item.variants && item.variants.length > 0
                      ? item.variants[0].id
                      : item.id,
                  sku: item.sku,
                  name: item.name || item.longDescription,
                  ean: item.matchedEan,
                }));
              }),
              catchError(() => of([])),
              tap(() => (this.isSearchingByEan = false))
            )
        )
      )
      .subscribe((data: Product[]) => (this.eanSearchResults = data));
  }

  private fetchStores(): void {
    this.inventoryService.getAllStores().subscribe({
      next: (response: any) => {
        // Assuming response is an array or has a data property
        const apiStores = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];

        this.stores = apiStores.map((store: any) => ({
          id: store.id,
          name: store.name || store.storeName, // Handling potential name fields
        }));

        // Auto-select first store if available (optional, maybe better to leave empty for multi)
        // for now keeping it empty to force user selection or select all?
        // Let's leave it empty as per typical multi-select behavior
      },
      error: (err) => {
        console.error('Error loading stores:', err);
        this.toastr.error('Could not load available stores.');
      },
    });
  }

  public onProductSelect(product: Product | null): void {
    this.searchParams.selectedProduct = product;
  }

  // No specific onStoreSelect needed if binding directly to searchParams.selectedStores

  public fetchMovementData(): void {
    const variantId = this.searchParams.selectedProduct?.id;
    const storeIds = this.searchParams.selectedStores?.map((s) => s.id) || [];
    const { fromDate, toDate } = this.searchParams;

    if (!variantId || storeIds.length === 0 || !fromDate || !toDate) {
      this.toastr.error(
        'Please select all required fields (Product, Stores, Dates).'
      );
      return;
    }

    this.loading = true;
    this.movementList = [];
    this.meta = null;

    this.stockMovementService
      .getStockMovement(
        variantId,
        storeIds,
        fromDate,
        toDate,
        this.currentPage,
        this.pageSize
      )
      .subscribe({
        next: (response) => {
          this.meta = response.meta;
          this.movementList = response.data || [];

          if (response.pagination) {
            this.totalItems = response.pagination.total;
            this.pageCount = response.pagination.totalPages;
            this.currentPage = response.pagination.page;
          } else {
            this.totalItems = this.movementList.length;
            this.pageCount = Math.ceil(this.totalItems / this.pageSize) || 1;
          }

          this.loading = false;

          if (this.movementList.length > 0) {
            this.toastr.success('Stock movement data loaded successfully.');
          } else {
            this.toastr.info('No stock movement records found.');
          }
        },
        error: (err) => {
          this.loading = false;
          console.error('Error fetching stock movement:', err);
          this.toastr.error('Failed to load stock movement data.');
        },
      });
  }

  public onSearch(): void {
    this.currentPage = 1;
    this.fetchMovementData();
  }

  public pageSizeChange(): void {
    this.currentPage = 1;
    this.fetchMovementData();
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.fetchMovementData();
  }

  /** Tooltip for the collapsed store selection. */
  public storeNames(items: Store[]): string {
    return (items || []).map((s) => s.name).join(', ');
  }

  public getPageNumbers(): number[] {
    const pages: number[] = [];
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(this.maxPageNumbers / 2)
    );
    let endPage = startPage + this.maxPageNumbers - 1;

    if (endPage > this.pageCount) {
      endPage = this.pageCount;
      startPage = Math.max(1, endPage - this.maxPageNumbers + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }
  // Modal properties
  public showModal = false;
  public selectedDetails: any[] = [];
  public selectedCategory = '';

  public openDetailPopup(items: any[], category: string): void {
    if (!items || items.length === 0) return;
    this.selectedDetails = items;
    this.selectedCategory = category;
    this.showModal = true;
  }

  public closeDetailPopup(): void {
    this.showModal = false;
    this.selectedDetails = [];
    this.selectedCategory = '';
  }
}
